import { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { BookOpen, Upload, Loader2, Trash2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function TurboLearn() {
  const [imports, setImports] = useState([]);
  const [title, setTitle] = useState("");
  const [course, setCourse] = useState("");
  const [rawText, setRawText] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try { const { data } = await api.get("/turbolearn"); setImports(data); } catch (err) { console.error("[ARIA]", err); }
  };
  useEffect(() => { load(); }, []);

  const importPaste = async () => {
    if (!rawText.trim()) { toast.error("Paste your TurboLearn export first"); return; }
    setBusy(true);
    try {
      await api.post("/turbolearn/import", { title: title || "TurboLearn import", raw_text: rawText, course });
      toast.success("Imported — extracting concepts…");
      setTitle(""); setRawText(""); setCourse("");
      load();
    } catch { toast.error("Import failed"); }
    finally { setBusy(false); }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    if (course) fd.append("course", course);
    setBusy(true);
    try {
      await api.post("/turbolearn/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${file.name}`);
      load();
    } catch { toast.error("Import failed"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    await api.delete(`/turbolearn/${id}`);
    load();
  };

  return (
    <div className="h-screen overflow-y-auto p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="font-display text-3xl mb-1">TurboLearn</h1>
        <div className="aria-card p-4 mb-6 border-amber-400/40 bg-amber-400/5" data-testid="turbolearn-honesty-notice">
          <p className="text-sm text-[var(--aria-text-muted)] leading-relaxed">
            <strong className="text-[var(--aria-text)]">TurboLearn has no public API</strong> — there's no live connection to fake here.
            Paste an exported note below, or upload a file, and ARIA will fold it into your knowledge system exactly like any other source.
          </p>
        </div>

        <div className="aria-card p-5 mb-8 space-y-3" data-testid="turbolearn-import-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Title (e.g. Week 4 — Gibbs Free Energy)"
              data-testid="turbolearn-title-input"
              className="px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none focus:ring-2 focus:ring-[var(--aria-primary)] text-sm" />
            <input value={course} onChange={e => setCourse(e.target.value)} placeholder="Course (optional)"
              data-testid="turbolearn-course-input"
              className="px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none focus:ring-2 focus:ring-[var(--aria-primary)] text-sm" />
          </div>
          <textarea value={rawText} onChange={e => setRawText(e.target.value)} rows={6} placeholder="Paste your TurboLearn export text here…"
            data-testid="turbolearn-paste-input"
            className="w-full px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none focus:ring-2 focus:ring-[var(--aria-primary)] text-sm" />
          <div className="flex justify-between items-center">
            <button onClick={() => fileRef.current?.click()} data-testid="turbolearn-upload-button"
              className="btn px-3 py-2 rounded-md border border-[var(--aria-border)] hover:border-[var(--aria-primary)] text-sm flex items-center gap-2">
              <Upload size={14} /> Upload file instead
            </button>
            <input ref={fileRef} type="file" hidden accept=".pdf,.txt,.md" onChange={e => uploadFile(e.target.files[0])} />
            <button onClick={importPaste} disabled={busy} data-testid="turbolearn-import-submit"
              className="btn btn-primary px-4 py-2 text-sm disabled:opacity-50 flex items-center gap-2">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <BookOpen size={14} />} Import
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {imports.map(t => (
            <div key={t.import_id} className="aria-card p-5" data-testid={`turbolearn-card-${t.import_id}`}>
              <div className="flex items-start justify-between mb-3">
                <BookOpen size={18} className="text-[var(--aria-primary)]" />
                <div className="flex items-center gap-2">
                  {t.status === "processing" && <Loader2 size={14} className="animate-spin text-amber-500" />}
                  {t.status === "ready" && <CheckCircle2 size={14} className="text-emerald-500" />}
                  {t.status === "error" && <XCircle size={14} className="text-red-500" />}
                  <button onClick={() => remove(t.import_id)} data-testid={`turbolearn-delete-${t.import_id}`} className="text-[var(--aria-text-muted)] hover:text-[var(--aria-danger)]"><Trash2 size={13} /></button>
                </div>
              </div>
              <h3 className="font-display text-lg leading-tight mb-1">{t.title}</h3>
              <p className="text-xs text-[var(--aria-text-muted)] mb-3">{t.method === "upload" ? "Uploaded file" : "Pasted export"}</p>
              {t.concepts?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {t.concepts.slice(0, 4).map(c => <span key={c} className="aria-pill text-[10px]">{c}</span>)}
                </div>
              )}
              {t.is_demo && <div className="mt-3 text-[10px] uppercase tracking-wider text-[var(--aria-text-faint)] font-mono">Demo data</div>}
            </div>
          ))}
          {imports.length === 0 && (
            <div className="col-span-full text-center py-16 text-[var(--aria-text-muted)]">No TurboLearn material imported yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}
